import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { api } from '@/lib/api';
import PageMeta from '@/components/PageMeta';
import SectionHeader from '@/components/SectionHeader';

export function ResearchList() {
  const [posts, setPosts] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    api.get('/research')
      .then((r) => setPosts(r.data))
      .catch(() => setError('We could not load the research journey at the moment.'));
  }, []);

  return (
    <div>
      <PageMeta
        title="Research journey"
        description="A research journey built on testing, failure, learning, and refinement."
      />
      <section className="vl-container py-16 lg:py-24">
        <p className="mono-label text-vl-violet">Research journey</p>
        <h1 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05] mt-5 text-vl-ink max-w-3xl">
          A research journey built on testing, failure, learning, and refinement.
        </h1>
        <p className="mt-6 text-lg text-vl-ink/70 max-w-2xl leading-relaxed">
          Honest movement technology must be developed around its limitations as well as its capabilities. The
          journey below is updated as our research progresses.
        </p>
      </section>

      <section className="bg-vl-bg2 border-y hairline">
        <div className="vl-container py-20 lg:py-28">
          <SectionHeader kicker="Timeline" title="Where we have been." />

          {error && (
            <p className="mt-12 text-vl-ink/70" data-testid="research-error">
              {error}
            </p>
          )}

          <ol className="mt-14 relative space-y-6 lg:space-y-8" data-testid="research-list">
            {posts === null && !error ? (
              <li className="text-vl-ink/55">Loading…</li>
            ) : (
              (posts || []).map((p) => (
                <li key={p.id} className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
                  <div className="lg:col-span-3">
                    <p className="mono-label text-vl-violet">{p.period}</p>
                  </div>
                  <div className="lg:col-span-9 rounded-3xl bg-vl-surface border hairline p-7 lg:p-8 hover:shadow-md transition-shadow">
                    <h3 className="font-serif-display text-2xl lg:text-3xl text-vl-ink leading-tight">
                      <Link to={`/research/${p.slug}`} data-testid={`research-link-${p.slug}`} className="hover:text-vl-violet transition-colors">
                        {p.title}
                      </Link>
                    </h3>
                    <p className="mt-3 text-vl-ink/70 leading-relaxed">{p.summary}</p>
                    <Link
                      to={`/research/${p.slug}`}
                      className="inline-flex items-center gap-2 mt-5 text-sm text-vl-ink border-b border-vl-ink/20 pb-1 hover:border-vl-ink transition-colors w-fit"
                    >
                      Read more
                    </Link>
                  </div>
                </li>
              ))
            )}
          </ol>
        </div>
      </section>
    </div>
  );
}

export function ResearchDetail() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    api.get(`/research/${slug}`)
      .then((r) => setPost(r.data))
      .catch(() => setError('We could not find that research entry.'));
  }, [slug]);

  if (error) {
    return (
      <div className="vl-container py-32 text-center">
        <PageMeta title="Research" />
        <p className="text-vl-ink/70">{error}</p>
        <Link to="/research" className="mt-6 inline-flex items-center gap-2 text-vl-ink underline">
          <ArrowLeft size={14} /> Back to the research journey
        </Link>
      </div>
    );
  }
  if (!post) {
    return <div className="vl-container py-32 text-center text-vl-ink/55">Loading…</div>;
  }

  return (
    <article>
      <PageMeta title={post.title} description={post.summary} />
      <div className="vl-container py-16 lg:py-24 max-w-3xl">
        <Link to="/research" className="inline-flex items-center gap-2 text-sm text-vl-ink/70 hover:text-vl-ink">
          <ArrowLeft size={14} /> Research journey
        </Link>
        <p className="mt-8 mono-label text-vl-violet">{post.period}</p>
        <h1 className="font-serif-display text-4xl sm:text-5xl text-vl-ink leading-[1.05] mt-3">{post.title}</h1>
        <p className="mt-6 text-lg text-vl-ink/70 leading-relaxed">{post.summary}</p>
        <div className="mt-10 space-y-5 text-[17px] text-vl-ink/80 leading-[1.75] whitespace-pre-line">
          {post.body}
        </div>
      </div>
    </article>
  );
}
